import {
  collection,
  getDocs,
  doc,
  setDoc,
  deleteDoc,
  limit,
  query,
} from "firebase/firestore";
import { ref, listAll, getMetadata } from "firebase/storage";
import { httpsCallable } from "firebase/functions";
import {
  CONFIG_FIELDS,
  HEALTH_COLLECTIONS,
  INDEX_RECOMMENDATIONS,
  PROBE_COLLECTIONS,
  VERIFY_TIMEOUT_MS,
  isMaskedOrEmpty,
  makeServiceResult,
  withTimeout,
} from "./connectionVerifyUtils";
import {
  classifyFirestoreTestError,
  cleanupHealthTestDocuments,
  createHealthTestDocId,
  healthTestCollectionName,
} from "./healthTestCollectionUtils";
import {
  logFirestoreOperationResult,
} from "./connectionTestRuntimeService";
import {
  AUTH_NOT_SIGNED_IN_MESSAGE,
  FIRESTORE_CONNECTION_VERIFIED_MESSAGE,
  HEALTH_CHECK_MODE_CONNECTION_ONLY,
  SECURITY_RULES_DENIED_MESSAGE,
  WRITE_TESTS_SKIPPED_MESSAGE,
} from "./firestoreTestMessages";

function isConnectionOnlyMode(runtime) {
  return runtime?.executionMode === HEALTH_CHECK_MODE_CONNECTION_ONLY;
}

function connectionOnlyFirestoreMessage(extra = "") {
  const base = `${FIRESTORE_CONNECTION_VERIFIED_MESSAGE} ${WRITE_TESTS_SKIPPED_MESSAGE}`;
  return extra ? `${base} · ${extra}` : base;
}

export function validateConfiguration(conn) {
  const fields = {};
  const issues = [];
  let allRequiredOk = true;

  CONFIG_FIELDS.forEach(({ key, label, required }) => {
    const rawKey = key === "firebaseProjectId" ? "firebaseProjectId" : key;
    const value = conn[rawKey] ?? conn[key];
    const present = !isMaskedOrEmpty(value);
    fields[key] = { label, present, value: present ? (key === "apiKey" ? "••••" : String(value)) : "" };
    if (required && !present) {
      allRequiredOk = false;
      issues.push({ field: key, message: `Missing ${label}`, severity: "error" });
    }
  });

  if (conn.region && !/^[a-z0-9-]+$/.test(conn.region)) {
    issues.push({ field: "region", message: "Invalid region format", severity: "warning" });
  }

  const message = allRequiredOk
    ? "All required Firebase config fields validated"
    : `Missing required fields: ${issues.filter((i) => i.severity === "error").map((i) => i.field).join(", ")}`;

  return {
    result: makeServiceResult(allRequiredOk ? "success" : "failed", message, 0, { fields }),
    issues,
    allRequiredOk,
  };
}

export async function checkAuthentication(runtime) {
  const t0 = performance.now();
  const auth = runtime?.auth;
  const latencyMs = Math.round(performance.now() - t0);

  if (!auth) {
    return makeServiceResult(
      "not_signed_in",
      AUTH_NOT_SIGNED_IN_MESSAGE,
      latencyMs,
      { code: "no-auth-sdk", mode: HEALTH_CHECK_MODE_CONNECTION_ONLY }
    );
  }

  const user = auth.currentUser;
  if (!user) {
    return makeServiceResult(
      "not_signed_in",
      AUTH_NOT_SIGNED_IN_MESSAGE,
      latencyMs,
      { code: "no-session", mode: HEALTH_CHECK_MODE_CONNECTION_ONLY, uid: null }
    );
  }

  const provider = user.providerData?.[0]?.providerId || (user.isAnonymous ? "anonymous" : "unknown");
  return makeServiceResult(
    "success",
    `Authenticated session active · ${user.email || user.uid} (${provider})`,
    latencyMs,
    {
      uid: user.uid,
      email: user.email || "",
      provider,
      anonymous: Boolean(user.isAnonymous),
      mode: "authenticated",
    }
  );
}

export async function checkFirestore(runtime) {
  const t0 = performance.now();
  const fs = runtime.firestore;
  const authenticated = Boolean(runtime.authenticated);
  const connectionOnly = isConnectionOnlyMode(runtime);
  const suite = runtime.suite || "health_check";
  const projectId = runtime.projectId || runtime.app?.options?.projectId || "";
  let readCollection = null;
  let rulesResponded = false;
  let lastError = null;

  for (const collName of HEALTH_COLLECTIONS) {
    try {
      await withTimeout(getDocs(query(collection(fs, collName), limit(1))), VERIFY_TIMEOUT_MS, "Firestore");
      readCollection = collName;
      logFirestoreOperationResult(suite, "Read Result", `success (${collName})`);
      break;
    } catch (e) {
      lastError = e;
      const classified = classifyFirestoreTestError(e, { authenticated, connectionOnly });
      logFirestoreOperationResult(suite, "Read Result", classified.userMessage);
      if (classified.kind === "rules_responded") {
        rulesResponded = true;
        readCollection = collName;
        break;
      }
      if (classified.kind === "permission_denied") {
        const latencyMs = Math.round(performance.now() - t0);
        return makeServiceResult(
          "permission_denied",
          classified.userMessage,
          latencyMs,
          { code: classified.code, database: "(default)", mode: "native", collection: collName, projectId }
        );
      }
      if (classified.kind === "unauthenticated" && !connectionOnly) {
        const latencyMs = Math.round(performance.now() - t0);
        return makeServiceResult("failed", classified.userMessage, latencyMs, { code: classified.code });
      }
      if (classified.kind === "invalid_argument") {
        continue;
      }
    }
  }

  const latencyMs = Math.round(performance.now() - t0);
  if (readCollection) {
    if (connectionOnly) {
      const detail = rulesResponded
        ? `security rules responded on ${readCollection}`
        : `read ${readCollection}`;
      return makeServiceResult(
        "success",
        connectionOnlyFirestoreMessage(detail),
        latencyMs,
        {
          collection: readCollection,
          database: "(default)",
          mode: "native",
          projectId,
          connectionOnly: true,
          rulesResponded,
        }
      );
    }
    return makeServiceResult(
      "success",
      `Firestore native mode · database (default) · read ${readCollection}`,
      latencyMs,
      { collection: readCollection, database: "(default)", mode: "native", projectId }
    );
  }

  const code = lastError?.code || "";
  const message = lastError?.message || "Firestore unreachable";
  const classified = lastError ? classifyFirestoreTestError(lastError, { authenticated, connectionOnly }) : null;
  if (classified?.kind === "rules_responded") {
    return makeServiceResult(
      "success",
      connectionOnlyFirestoreMessage(`security rules responded · project ${projectId}`),
      latencyMs,
      { code: classified.code, database: "(default)", mode: "native", projectId, connectionOnly: true, rulesResponded: true }
    );
  }
  if (classified?.kind === "unauthenticated" && !connectionOnly) {
    return makeServiceResult("failed", classified.userMessage, latencyMs, { code: classified.code });
  }
  if (classified?.kind === "invalid_argument") {
    return makeServiceResult(
      "failed",
      classified.userMessage,
      latencyMs,
      { code: classified.code, collection: healthTestCollectionName() }
    );
  }
  if (code === "unavailable") {
    return makeServiceResult("failed", "Firestore unavailable — verify project ID and network", latencyMs, { code });
  }
  if (message.includes("timed out")) {
    return makeServiceResult("failed", "Firestore request timed out", latencyMs, { code: "timeout" });
  }
  if (code === "failed-precondition") {
    return makeServiceResult("failed", "Missing Firestore index (failed-precondition)", latencyMs, { code });
  }
  return makeServiceResult("failed", message, latencyMs, { code });
}

export async function runPermissionTests(runtime) {
  const fs = runtime.firestore;
  const authenticated = Boolean(runtime.authenticated);
  const connectionOnly = isConnectionOnlyMode(runtime);
  const suite = runtime.suite || "health_check";
  const collName = healthTestCollectionName();
  const testId = createHealthTestDocId("verify");
  const permissions = {
    read: { status: "unknown", message: "" },
    write: { status: "unknown", message: "" },
    delete: { status: "unknown", message: "" },
  };
  let wroteTestDoc = false;

  try {
    try {
      await getDocs(query(collection(fs, collName), limit(1)));
      permissions.read = { status: "success", message: "Read permitted" };
      logFirestoreOperationResult(suite, "Read Result", "success");
    } catch (e) {
      const classified = classifyFirestoreTestError(e, { authenticated, connectionOnly });
      if (classified.kind === "rules_responded") {
        permissions.read = { status: "success", message: classified.userMessage };
      } else {
        permissions.read = {
          status: classified.kind === "permission_denied" ? "denied" : "failed",
          message: classified.userMessage,
          code: classified.code,
        };
      }
      logFirestoreOperationResult(suite, "Read Result", classified.userMessage);
    }

    if (connectionOnly) {
      permissions.write = { status: "skipped", message: WRITE_TESTS_SKIPPED_MESSAGE, code: "no-session" };
      permissions.delete = { status: "skipped", message: WRITE_TESTS_SKIPPED_MESSAGE, code: "no-session" };
      logFirestoreOperationResult(suite, "Write Result", "skipped");
      logFirestoreOperationResult(suite, "Delete Result", "skipped");
      return permissions;
    }

    try {
      const testRef = doc(fs, collName, testId);
      await setDoc(testRef, { _healthCheck: true, createdAt: new Date().toISOString() });
      wroteTestDoc = true;
      permissions.write = { status: "success", message: "Write permitted" };
      logFirestoreOperationResult(suite, "Write Result", "success");

      try {
        await deleteDoc(testRef);
        wroteTestDoc = false;
        permissions.delete = { status: "success", message: "Delete permitted — test document removed" };
        logFirestoreOperationResult(suite, "Delete Result", "success");
      } catch (e) {
        const classified = classifyFirestoreTestError(e, { authenticated });
        permissions.delete = {
          status: classified.kind === "permission_denied" ? "read_only" : "failed",
          message:
            classified.kind === "permission_denied"
              ? SECURITY_RULES_DENIED_MESSAGE
              : classified.userMessage,
          code: classified.code,
        };
        logFirestoreOperationResult(suite, "Delete Result", classified.userMessage);
      }
    } catch (e) {
      const classified = classifyFirestoreTestError(e, { authenticated });
      logFirestoreOperationResult(suite, "Write Result", classified.userMessage);
      logFirestoreOperationResult(suite, "Delete Result", "skipped");
      if (classified.kind === "permission_denied") {
        permissions.write = {
          status: "read_only",
          message: SECURITY_RULES_DENIED_MESSAGE,
          code: classified.code,
        };
        permissions.delete = {
          status: "read_only",
          message: SECURITY_RULES_DENIED_MESSAGE,
          code: classified.code,
        };
      } else if (classified.kind === "invalid_argument") {
        permissions.write = { status: "failed", message: classified.userMessage, code: classified.code };
        permissions.delete = { status: "skipped", message: "Skipped — invalid Firestore path" };
      } else {
        permissions.write = { status: "failed", message: classified.userMessage, code: classified.code };
        permissions.delete = { status: "skipped", message: "Skipped — write failed" };
      }
    }
  } finally {
    if (wroteTestDoc) {
      await cleanupHealthTestDocuments(fs, [testId], collName);
    }
    logFirestoreOperationResult(suite, "Update Result", "not run");
    logFirestoreOperationResult(suite, "Batch Result", "not run");
    logFirestoreOperationResult(suite, "Transaction Result", "not run");
  }

  return permissions;
}

export async function runCollectionTests(runtime) {
  const fs = runtime.firestore;
  const accessible = [];
  let totalSampleDocs = 0;
  let largest = { name: "", count: 0, estimatedBytes: 0 };

  for (const name of PROBE_COLLECTIONS) {
    try {
      const snap = await withTimeout(
        getDocs(query(collection(fs, name), limit(50))),
        VERIFY_TIMEOUT_MS,
        `Collection ${name}`
      );
      if (!snap.empty || snap.docs.length >= 0) {
        const count = snap.size;
        let bytes = 0;
        const fieldSet = new Set();
        const samples = [];
        let largestDocBytes = 0;
        snap.docs.forEach((d) => {
          const data = d.data();
          const docBytes = JSON.stringify(data).length;
          bytes += docBytes;
          if (docBytes > largestDocBytes) largestDocBytes = docBytes;
          Object.keys(data || {}).forEach((k) => fieldSet.add(k));
          if (samples.length < 3) {
            samples.push({ id: d.id, bytes: docBytes, data });
          }
        });
        accessible.push({
          name,
          count,
          estimatedBytes: bytes,
          fields: Array.from(fieldSet),
          samples,
          largestDocBytes,
        });
        totalSampleDocs += count;
        if (count > largest.count) {
          largest = { name, count, estimatedBytes: bytes };
        }
      }
    } catch {
      /* collection not accessible */
    }
  }

  const avgSize = totalSampleDocs > 0
    ? Math.round(accessible.reduce((s, c) => s + c.estimatedBytes, 0) / totalSampleDocs)
    : 0;

  return {
    totalAccessible: accessible.length,
    collections: accessible,
    sampleDocuments: totalSampleDocs,
    largestCollection: largest.name || "—",
    largestCount: largest.count,
    estimatedDocuments: totalSampleDocs,
    averageDocumentBytes: avgSize,
  };
}

export function detectIndexes(permissionResults, firestoreResult) {
  const missing = [...INDEX_RECOMMENDATIONS];
  const required = [];

  if (firestoreResult?.code === "failed-precondition") {
    required.push({
      collection: healthTestCollectionName(),
      fields: ["createdAt"],
      reason: "Query requires composite index",
    });
  }

  return { recommendations: missing, required };
}

export async function checkStorage(runtime, conn) {
  if (isMaskedOrEmpty(conn.storageBucket)) {
    return makeServiceResult("not_configured", "Not Configured", 0, { code: "not-configured" });
  }

  const t0 = performance.now();
  try {
    const storage = runtime.storage;
    if (!storage) {
      return makeServiceResult("not_configured", "Not Configured", 0, { code: "not-configured" });
    }
    const bucket = conn.storageBucket;

    try {
      const rootRef = ref(storage, "/");
      await withTimeout(getMetadata(rootRef), VERIFY_TIMEOUT_MS, "Storage metadata");
    } catch {
      /* metadata may fail on root — continue */
    }

    try {
      await withTimeout(listAll(ref(storage, "/")), VERIFY_TIMEOUT_MS, "Storage list");
      const latencyMs = Math.round(performance.now() - t0);
      return makeServiceResult(
        "success",
        `Storage bucket ${bucket} reachable · list permitted`,
        latencyMs,
        { bucket }
      );
    } catch (e) {
      const latencyMs = Math.round(performance.now() - t0);
      const code = e?.code || "";
      if (code === "storage/unauthorized" || code === "storage/unauthenticated") {
        return makeServiceResult(
          "success",
          `Storage bucket ${bucket} exists · list permission denied (read-only bucket)`,
          latencyMs,
          { bucket, code }
        );
      }
      if (code === "storage/object-not-found" || code === "storage/bucket-not-found") {
        return makeServiceResult("not_configured", "Not Configured", latencyMs, { code });
      }
      if (String(e?.message || "").includes("timed out")) {
        return makeServiceResult("failed", "Storage request timed out", latencyMs, { code: "timeout" });
      }
      return makeServiceResult("warning", e?.message || "Storage bucket exists but list unavailable", latencyMs, { code });
    }
  } catch (e) {
    const latencyMs = Math.round(performance.now() - t0);
    const code = e?.code || "";
    if (code.includes("not-found") || code === "storage/bucket-not-found") {
      return makeServiceResult("not_configured", "Not Configured", latencyMs, { code });
    }
    return makeServiceResult("not_configured", "Not Configured", latencyMs, { code });
  }
}

export async function checkFunctions(runtime, conn) {
  const region = String(conn.region || "").trim();
  if (!region) {
    return makeServiceResult("not_configured", "Not Configured", 0, { code: "not-configured" });
  }

  const t0 = performance.now();
  try {
    const functions = runtime.functions;
    if (!functions) {
      return makeServiceResult("not_configured", "Not Configured", 0, { code: "not-configured" });
    }
    try {
      await withTimeout(httpsCallable(functions, "healthCheck")({}), 8000, "Functions callable");
      const latencyMs = Math.round(performance.now() - t0);
      return makeServiceResult("success", `Callable endpoint reachable (region: ${region})`, latencyMs, { region });
    } catch (e) {
      const latencyMs = Math.round(performance.now() - t0);
      const code = e?.code || "";
      if (code === "functions/not-found" || code === "functions/unavailable") {
        return makeServiceResult(
          "success",
          `Functions SDK initialized (region: ${region}) · healthCheck callable not deployed`,
          latencyMs,
          { region, code }
        );
      }
      return makeServiceResult("warning", e?.message || "Functions region configured", latencyMs, { region, code });
    }
  } catch (e) {
    const latencyMs = Math.round(performance.now() - t0);
    return makeServiceResult("failed", e?.message || "Functions initialization failed", latencyMs, { code: e?.code, region });
  }
}

export async function checkHosting(conn) {
  const explicitUrl = String(conn.hostingUrl || "").trim();
  const autoUrl = conn.firebaseProjectId ? `https://${conn.firebaseProjectId}.web.app` : "";
  const url = explicitUrl || autoUrl;

  if (!url) {
    return makeServiceResult("not_configured", "Hosting not configured.", 0, { code: "not-configured" });
  }

  const t0 = performance.now();
  try {
    const res = await withTimeout(fetch(url, { method: "HEAD", cache: "no-store" }), VERIFY_TIMEOUT_MS, "Hosting");
    const latencyMs = Math.round(performance.now() - t0);

    if (res.ok || res.status === 401 || res.status === 403) {
      return makeServiceResult("success", `Hosting site reachable (HTTP ${res.status})`, latencyMs, { url });
    }
    if (res.status === 404) {
      if (explicitUrl) {
        return makeServiceResult("failed", `Hosting configured but returned HTTP 404`, latencyMs, { url });
      }
      return makeServiceResult("not_configured", "Hosting not configured.", latencyMs, { url });
    }
    if (explicitUrl) {
      return makeServiceResult("failed", `Hosting configured but returned HTTP ${res.status}`, latencyMs, { url });
    }
    return makeServiceResult("not_configured", "Hosting not configured.", latencyMs, { url });
  } catch (e) {
    const latencyMs = Math.round(performance.now() - t0);
    const message = e?.message || "";
    if (explicitUrl) {
      if (message.includes("timed out")) {
        return makeServiceResult("failed", "Hosting configured but request timed out", latencyMs, { url, code: "timeout" });
      }
      return makeServiceResult("failed", `Hosting configured but unreachable: ${message}`, latencyMs, { url });
    }
    return makeServiceResult("not_configured", "Hosting not configured.", latencyMs, { url });
  }
}

export function checkAppCheck(conn) {
  if (conn.appCheckSiteKey) {
    return makeServiceResult("success", "App Check site key configured", 0, { enabled: true });
  }
  return makeServiceResult("not_configured", "Not Configured", 0, { enabled: false });
}

export function checkAnalytics(conn) {
  if (!isMaskedOrEmpty(conn.measurementId)) {
    return makeServiceResult("success", `Analytics configured (measurement ID present)`, 0, { enabled: true });
  }
  return makeServiceResult("not_configured", "Not Configured", 0, { enabled: false });
}

export function checkMessaging(conn) {
  if (!isMaskedOrEmpty(conn.messagingSenderId)) {
    return makeServiceResult("success", "Cloud Messaging sender ID configured", 0, { enabled: true });
  }
  return makeServiceResult("not_configured", "Not Configured", 0, { enabled: false });
}

export function checkCrashlytics() {
  return makeServiceResult(
    "not_configured",
    "Not Configured (Crashlytics requires native/mobile SDK)",
    0,
    { enabled: false }
  );
}

export function runSecurityAudit(conn, runtimeAppName) {
  const findings = [];

  if (isMaskedOrEmpty(conn.apiKey)) {
    findings.push({ type: "missing_api_key", severity: "error", message: "API key missing" });
  }
  if (!conn.firebaseProjectId) {
    findings.push({ type: "invalid_project", severity: "error", message: "Project ID missing" });
  }
  if (typeof navigator !== "undefined" && !navigator.onLine) {
    findings.push({ type: "offline", severity: "error", message: "Browser offline" });
  }
  if (runtimeAppName?.startsWith("runtime_")) {
    findings.push({
      type: "shared_runtime",
      severity: "info",
      message: "Health Check uses the shared Studio connection runtime cache",
    });
  }

  findings.push({
    type: "rules_note",
    severity: "info",
    message: "Firestore rules should restrict write access — verify in Firebase Console",
  });

  return findings;
}

export async function runFullEngine(runtime, conn, initLatencyMs) {
  const checks = [];
  const services = {};
  const performance = { init: initLatencyMs };

  const configValidation = validateConfiguration(conn);
  services.configuration = configValidation.result;
  pushCheckTo(checks, "configuration", configValidation.result);

  const authResult = await checkAuthentication(runtime);
  services.authentication = authResult;
  performance.auth = authResult.latencyMs;
  pushCheckTo(checks, "authentication", authResult);

  const firestoreResult = await checkFirestore(runtime);
  const permissions = await runPermissionTests(runtime);
  const collections = await runCollectionTests(runtime);

  services.firestore = firestoreResult;
  performance.firestore = firestoreResult.latencyMs;
  pushCheckTo(checks, "firestore", firestoreResult);

  const indexes = detectIndexes(permissions, firestoreResult);

  const storageResult = await checkStorage(runtime, conn);
  services.storage = storageResult;
  performance.storage = storageResult.latencyMs;
  pushCheckTo(checks, "storage", storageResult);

  const functionsResult = await checkFunctions(runtime, conn);
  services.functions = functionsResult;
  performance.functions = functionsResult.latencyMs;
  pushCheckTo(checks, "functions", functionsResult);

  const hostingResult = await checkHosting(conn);
  services.hosting = hostingResult;
  performance.hosting = hostingResult.latencyMs;
  pushCheckTo(checks, "hosting", hostingResult);

  const appCheckResult = checkAppCheck(conn);
  services.appCheck = appCheckResult;
  pushCheckTo(checks, "appCheck", appCheckResult);

  const analyticsResult = checkAnalytics(conn);
  services.analytics = analyticsResult;
  pushCheckTo(checks, "analytics", analyticsResult);

  const messagingResult = checkMessaging(conn);
  services.messaging = messagingResult;
  pushCheckTo(checks, "messaging", messagingResult);

  const crashlyticsResult = checkCrashlytics();
  services.crashlytics = crashlyticsResult;
  pushCheckTo(checks, "crashlytics", crashlyticsResult);

  const security = runSecurityAudit(conn, runtime.app?.name);

  return {
    checks,
    services,
    permissions,
    collections,
    indexes,
    security,
    performance,
    configFields: configValidation.result.fields,
    configIssues: configValidation.issues,
  };
}

function pushCheckTo(checks, service, result) {
  checks.push({
    service,
    status: result.status,
    message: result.message,
    latencyMs: result.latencyMs ?? 0,
    ...(result.code ? { code: result.code } : {}),
  });
}
